import React, { useState } from 'react';
import {
  X,
  Users,
  UserPlus,
  LogOut,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ChatModalPortal from './ChatModalPortal';

export const GroupInfoDialog = ({
  open,
  onClose,
  group,
  members = [],
  allUsers = [],
  currentUserId,
  canAddMembers = true,
  onAddMember,
  onLeaveGroup,
}) => {
  const [showAddMember, setShowAddMember] = useState(false);
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open || !group) return null;

  const memberUserIds = new Set(members.map((m) => m.id || m.user_id));
  const availableToAdd = allUsers.filter(
    (u) => !memberUserIds.has(u.id) && u.id !== currentUserId
  );

  const displayName = (profile) => (
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Student'
  );

  const filteredAvailable = availableToAdd.filter((u) => {
    const q = search.toLowerCase();
    return (
      displayName(u).toLowerCase().includes(q) ||
      (u.profession || '').toLowerCase().includes(q)
    );
  });

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <ChatModalPortal>
      <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl border-0 bg-white shadow-2xl flex flex-col max-h-[85vh]">
        <div aria-hidden="true" className="flex h-1.5 w-full shrink-0">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <div className="relative border-b border-zinc-100 bg-white p-6 pb-8 text-center text-zinc-950">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Mbyll dritaren"
            title="Mbyll"
          >
            <X size={18} />
          </button>

          <div className="relative mx-auto mt-2 mb-3 flex h-20 w-20 items-center justify-center rounded-2xl bg-zinc-950 text-[#FFCE00] text-2xl font-black shadow-xl ring-4 ring-amber-100">
            <Users size={32} />
          </div>

          <h3 className="text-lg font-bold tracking-tight text-zinc-950">{group.name || 'Grup Mësimor'}</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Grup Studimi Gjermanisht • {members.length} anëtarë
          </p>

          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-800">
            <Users size={12} className="text-[#DD0000]" />
            <span>Grup studimi</span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {actionError && <p role="alert" className="text-xs text-red-600">{actionError}</p>}
          {/* Members list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                Anëtarët ({members.length})
              </span>
              {canAddMembers && (
                <button
                  type="button"
                  onClick={() => {
                    setShowAddMember((p) => !p);
                    setActionError('');
                  }}
                  className="text-xs font-bold text-zinc-950 hover:text-[#DD0000] flex items-center gap-1"
                >
                  <UserPlus size={14} />
                  {showAddMember ? 'Mbyll' : 'Shto Anëtar'}
                </button>
              )}
            </div>

            {/* Add Member Drawer */}
            {showAddMember && (
              <div className="mb-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 space-y-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kërko studentë për të shtuar..."
                  className="h-8 text-xs bg-white rounded-xl"
                />
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {filteredAvailable.length === 0 ? (
                    <p className="text-center text-[11px] text-zinc-400 py-2">
                      Nuk ka anëtarë të tjerë për të shtuar.
                    </p>
                  ) : (
                    filteredAvailable.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-2 rounded-xl bg-white border border-zinc-100"
                      >
                        <span className="text-xs font-semibold text-zinc-800 truncate">
                          {displayName(u)}
                        </span>
                        <Button
                          size="sm"
                          disabled={saving}
                          onClick={async () => {
                            setSaving(true);
                            setActionError('');
                            try {
                              const added = await onAddMember(u.id);
                              if (added === false) {
                                setActionError('Anëtari nuk u shtua. Provoni përsëri.');
                                return;
                              }
                              setShowAddMember(false);
                              setSearch('');
                            } catch (error) {
                              console.error('Unable to add group member:', error);
                              setActionError('Anëtari nuk u shtua. Provoni përsëri.');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          className="h-6 px-2.5 text-[10px] rounded-lg bg-zinc-950 text-[#FFCE00] font-bold"
                        >
                          Shto
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {members.map((m) => {
                const isMe = m.id === currentUserId || m.user_id === currentUserId;
                const name = displayName(m);
                return (
                  <div
                    key={m.id || m.user_id}
                    className="flex items-center justify-between p-2 rounded-xl bg-zinc-50 border border-zinc-100"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-200 text-xs font-bold text-zinc-800">
                        {getInitials(name)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-zinc-900 truncate">
                          {name} {isMe && '(Ti)'}
                        </p>
                        <p className="text-[10px] text-zinc-500 truncate">
                          {m.profession || 'Student'}
                        </p>
                      </div>
                    </div>
                    {isMe ? (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        Ti
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-400 bg-zinc-200 px-2 py-0.5 rounded-full">
                        Anëtar
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Danger zone */}
          <div className="pt-3 border-t border-zinc-100">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setActionError('');
                Promise.resolve(onLeaveGroup())
                  .then((left) => {
                    if (left !== false) onClose();
                  })
                  .catch((error) => {
                    console.error('Unable to leave group:', error);
                    setActionError('Nuk mund të dilni nga grupi. Provoni përsëri.');
                  });
              }}
              className="w-full rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 font-bold gap-2 text-xs h-10"
            >
              <LogOut size={15} />
              Dil nga ky grup
            </Button>
          </div>
        </div>
      </div>
      </div>
    </ChatModalPortal>
  );
};

export default GroupInfoDialog;
